import { Modal, SubmitButton, Switch } from "@xola/ui-kit";
import { nullableNumberAtleastZero, SecondaryButton } from "common";
import pluralize from "pluralize";
import React from "react";
import { object } from "yup";
import { NumberInlineValuePopover } from "@/components/Common/NumberInlineValuePopover";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { useVisibility } from "@/hooks/useVisibility";

interface SetVariantLowInventoryModalProps {
    isOpen: boolean;
    selectedCount: number;
    onClose: () => void;
    onApply: (threshold: number) => Promise<void> | void;
}

interface LowInventoryFormValues {
    notifyLowInventory: boolean;
    lowInventoryThreshold: number;
}

const validationSchema = object({
    lowInventoryThreshold: nullableNumberAtleastZero(),
});

const initialValues: LowInventoryFormValues = { notifyLowInventory: true, lowInventoryThreshold: 500 };

export const SetVariantLowInventoryModal = ({
    isOpen,
    selectedCount,
    onClose,
    onApply,
}: SetVariantLowInventoryModalProps) => {
    const [isThresholdPopoverOpen, { toggle: toggleThresholdPopover }] = useVisibility(false);

    const handleSubmit = async (values: LowInventoryFormValues) => {
        try {
            await onApply(values.notifyLowInventory ? Number(values.lowInventoryThreshold) : 0);
            onClose();
        } catch {
            // onApply already flashed the error; keep the modal open so it can be retried
        }
    };

    return (
        <Modal size="small" isOpen={isOpen} onClose={onClose}>
            <XolaFormik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ values, setFieldValue, handleSubmit, isSubmitting }) => (
                    <>
                        <Modal.Header>Set Variant Low Inventory Alert Levels</Modal.Header>

                        <Modal.Body>
                            <div className="mb-4 rounded bg-blue-lighter p-3 text-sm">
                                This change will be applied to <var data-var="selectedCount">{selectedCount}</var>{" "}
                                selected <var data-var="variantLabel">{pluralize("variant", selectedCount)}</var>.
                            </div>

                            <Switch.Group>
                                <Switch
                                    isChecked={values.notifyLowInventory}
                                    onChange={(checked: boolean) => setFieldValue("notifyLowInventory", checked)}
                                />
                                <Switch.Label className="ml-3 whitespace-nowrap">
                                    Notify me if inventory falls below
                                    {values.notifyLowInventory && (
                                        <>
                                            &nbsp;
                                            <span onClick={(e) => e.stopPropagation()}>
                                                <NumberInlineValuePopover
                                                    isOpen={isThresholdPopoverOpen}
                                                    value={values.lowInventoryThreshold}
                                                    text={values.lowInventoryThreshold.toString()}
                                                    min={0}
                                                    onOpenChange={toggleThresholdPopover}
                                                    onChange={(value: number) =>
                                                        setFieldValue("lowInventoryThreshold", value)
                                                    }
                                                />
                                            </span>
                                        </>
                                    )}
                                </Switch.Label>
                            </Switch.Group>
                        </Modal.Body>

                        <Modal.Footer>
                            <SecondaryButton disabled={isSubmitting} onClick={onClose}>
                                Cancel
                            </SecondaryButton>
                            <SubmitButton type="submit" isLoading={isSubmitting} onClick={handleSubmit}>
                                Apply
                            </SubmitButton>
                        </Modal.Footer>
                    </>
                )}
            </XolaFormik>
        </Modal>
    );
};
